import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { saveAs } from "file-saver";
import { LoadingController, ToastController } from "ionic-angular";
import moment from "moment";
import { Observable } from "rxjs/Observable";
import { of } from "rxjs/observable/of";
import { catchError, delay, finalize, shareReplay } from "rxjs/operators";
import { AuthService } from "../../app/auth/auth.service";
import { visitFreqConfig } from "../../configs/visit-freq-config";
import { MockCreatinineData } from "../../mocks/mock-creatinine";
import { CreatinineData } from "../../models/creatinine/creatinine-data";
import OrderItem from "../../models/order-item";
import Order from "../../models/orders/order";
import { ConfigurationService } from "../configuration/configuration.service";
import { NavBarServiceProvider } from "../nav-bar-service/nav-bar-service";
import { ResourceManagementService } from "../resource-management/resource-management.service";
import { StateManagementService } from "../state-management/state-management.service";

@Injectable()
export class OrderDataService {
  spinner = null;
  spinnerCounter = 0;
  private openOrdersCache = new Map<string, Observable<any>>();

  patChartId: string;
  idgDataUrl: string;
  reportsUrl: string;
  medicationsUrl: string;
  assessmentUrl: string;
  headers: any;
  prnFeatureToggleOn: boolean;

  constructor(
    private authSvc: AuthService,
    public http: HttpClient,
    public toastCtrl: ToastController,
    public loadingCtrl: LoadingController,
    public stateData: StateManagementService,
    private config: ConfigurationService,
    private navBarServiceProvider: NavBarServiceProvider,
    private resourceManagementService: ResourceManagementService
  ) {
    this.headers = new HttpHeaders().set("Content-Type", "application/json");

    this.reportsUrl = config.getConfig().reportsUrl;
    this.medicationsUrl = config.getConfig().medicationUrl;
    this.assessmentUrl = config.getConfig().apiUrl;
    this.idgDataUrl = config.resolveIdgUrl();
    this.prnFeatureToggleOn = this.navBarServiceProvider.prnFeatureToggleOn();
  }

  //used to avoid having to pass enums through to a function
  //when submitting VF data
  roleEnums: any = [];

  orderTypeEndpoints = {
    MC: "medications",
    TM: "treatments",
    EQ: "equipments",
    SP: "supplies",
    DG: "diagnosis",
    LC: "levelsOfCare",
    VF: "visitFrequencies",
    OT: "others",
    RC: "recertifications",
  };

  ionViewCanEnter() {
    return this.authSvc.authenticated();
  }

  /** New Orders */

  getNewOrders(patientId): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    let params = new HttpParams()
      .set("size", "100")
      .set("status", "ST")
      .set("sort", "createdDatetime,desc")
      .set("patient_id", patientId);

    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/items`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            let orderItems = this.combineOrderAndTypeSpecificData(
              data["content"]
            );
            observer.next(this.convertOrderItemsToOrders(orderItems));
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.presentErrorToast(err, "Failed to load new order.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  public getIdForQBOSId(qbosId: any): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return new Observable((observer) => {
      this.http
        .get(
          `${this.idgDataUrl}/company-resources/patient-charts/${qbosId}?externalId=true`,
          { headers: headers }
        )
        .subscribe(
          (data) => {
            observer.next(data["id"]);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.presentErrorToast(err, "Failed to load patient id");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  public patientInfoFromQBOSId(qbosId): Observable<any> {
    if (!qbosId) return new Observable((observer) => observer.complete());

    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return new Observable((observer) => {
      this.http
        .get(
          `${this.idgDataUrl}/company-resources/patient-charts/${qbosId}?externalId=true`,
          { headers: headers }
        )
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.presentErrorToast(err, "Failed to load patient data");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  public getMedications(searchTerm): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return new Observable((observer) => {
      this.http
        .get(
          `${this.medicationsUrl}/lookup?name=${encodeURIComponent(
            searchTerm
          )}&pageSize=100`,
          { headers: headers }
        )
        .subscribe(
          (data) => {
            observer.next(data["results"]);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.presentErrorToast(err, "Failed to load medications.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  public getDiagnosisOptions(searchTerm): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");
    let params = new HttpParams().set("search", encodeURIComponent(searchTerm));

    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/lookups/idg/diagnosis`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            observer.next(data["content"]);
            observer.complete();
          },
          (err) => {
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  //default value is false for creatinine clearance, won't be needed unless user is older than 18
  getMedicationInformation(
    medId,
    birthdate,
    weight,
    creatinineData: CreatinineData
  ) {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    if (!birthdate) birthdate = "1995-05-30";

    if (!weight) weight = "100";
    //const { creatinineClearance, serumCreatinine } = creatinineData;
    let creatinineClearance = null;
    let serumCreatinine = null;
    const params = new HttpParams()
      .set("patientBirthdate", birthdate ? birthdate : null)
      .set("weight", weight ? `${weight} kg` : null)
      .set(
        "creatinineClearance",
        creatinineClearance ? `${creatinineClearance}` : null
      )
      .set("serumCreatinine", serumCreatinine ? `${serumCreatinine}` : null);

    return new Observable((observer) => {
      this.http
        .get(`${this.medicationsUrl}/get/${medId}`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            let response = this.trimMedicationData(data);
            observer.next(response);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            //this.presentErrorToast(err, "Failed to load medication dosing information");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  trimMedicationData(data: any) {
    let output = {
      name: data.name,
      doseForm: data.doseForm,
      route: data.route,
      strength: data.strength,
      gpi: data.gpi,
      dosageParameters: [],
      frequency: [],
      dosage: [],
    };
    let orders = data.orders.sort((a, b) => {
      const aString = a.doseStrength || "";
      const bString = b.doseStrength || "";
      return !aString ? 1 : aString.localeCompare(bString);
    });
    orders.forEach((order) => {
      let formAndStrength = "";
      const orderDose = order.doseForm ? order.doseForm : order.doseStrength;
      if (order.formAndStrength) {
        formAndStrength = order.formAndStrength;
      } else {
        formAndStrength =
          `${order.verb} ${orderDose} ${order.adverb} ${order.frequency} ${order.scheduleOfAdministration}`.trim();
      }
      const firstLetter = formAndStrength[0] || formAndStrength.charAt(0);
      let frequency = order["scheduleOfAdministration"]
        ? `${order.frequency} ${order.scheduleOfAdministration}`
        : order.frequency;
      formAndStrength = firstLetter
        ? firstLetter.toUpperCase() + formAndStrength.slice(1)
        : "";
      if (output["dosageParameters"].indexOf(formAndStrength) == -1) {
        output["dosageParameters"].push(
          formAndStrength == "N/A" ? "Other" : formAndStrength
        );
        output["frequency"].push(frequency);
        output["dosage"].push(orderDose);
      }
    });
    return output;
  }

  public getFacilities(): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");
    let params = new HttpParams().set("size", "9999");

    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/company-resources/idg/facilities`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            observer.next(data["content"]);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.presentErrorToast(err, "Failed to load facilities");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  public getContacts(patientid: string): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");
    let params = new HttpParams().set("size", "9999");
    let patientChartInfo = JSON.parse(
      window.sessionStorage.getItem("patientChartInfo")
    );

    return new Observable((observer) => {
      this.http
        .get(
          `${this.idgDataUrl}/patient-charts/` +
            patientChartInfo.id +
            "/contacts",
          { headers: headers, params: params }
        )
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.presentErrorToast(err, "Failed to load facilities");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  public createOrder(data: any) {
    if (data) {
      return this.http.post(`${this.idgDataUrl}/orders`, data);
    }
  }

  submitTypeSpecificData(type, data): Observable<any> {
    if (data) {
      return this.http.post(
        `${this.idgDataUrl}/orders/${this.orderTypeEndpoints[type]}`,
        data
      );
    }
  }

  editTypeSpecificData(type, data, order, callback) {
    if (data) {
      try {
        this.http
          .put(
            `${this.idgDataUrl}/orders/${this.orderTypeEndpoints[type]}`,
            data
          )
          .subscribe(
            (data) => {
              order["orderTypeId"] = data["id"];
              callback(order);
            },
            (err) => {
              this.presentErrorToast(
                err,
                "Error submitting type specific data"
              );
            }
          );
      } catch (error) {
        console.error("Error during order submission.", error);
      }
    }
  }

  retrieveTypeSpecificData(type, id): Observable<any> {
    return new Observable((observer) => {
      if (!type) {
        observer.next(null);
        observer.complete();
      }
      this.http
        .get(`${this.idgDataUrl}/orders/${this.orderTypeEndpoints[type]}/${id}`)
        .subscribe(
          (data) => {
            if (data) {
              delete data["id"];
              observer.next(data);
            } else {
              observer.next(null);
            }
            observer.complete();
          },
          (err) => {
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  public editOrder(data: any) {
    if (data) {
      return this.http.put(`${this.idgDataUrl}/orders`, data);
    }
  }

  //I think this is outdated since the orderStatus update
  public startOrder(order: any) {
    //TODO: See if order needs to first be updated with approved by or if that's handled elsewhere
    try {
      order.orderItems.forEach((item) => {
        this.increaseSpinnerCount();
        this.http
          .put(
            `${this.idgDataUrl}/orders/${order.id}/items/${item.id}/start`,
            item
          )
          .subscribe(
            (data) => {
              this.decreaseSpinnerCount();
            },
            (err) => {
              this.decreaseSpinnerCount();
              this.presentErrorToast(err, "Error Starting Order");
            }
          );
      });
    } catch (error) {
      console.error("Error starting order");
    }
  }

  public openOrder(order: any, prnVisitToggle: boolean, callback) {
    //TODO: See if order needs to first be updated with approved by or if that's handled elsewhere
    try {
      order.orderItems.forEach((item) => {
        this.increaseSpinnerCount();
        this.http
          .put(
            `${this.idgDataUrl}/orders/${order.id}/items/${item.id}/open?prnVisitToggle=${prnVisitToggle}`,
            { headers: this.headers }
          )
          .subscribe(
            (data) => {
              this.decreaseSpinnerCount();
              callback();
            },
            (err) => {
              this.decreaseSpinnerCount();
              this.presentErrorToast(err, "Error Opening Order");
            }
          );
      });
    } catch (error) {
      console.error("Error opening order");
    }
  }

  /** Open Orders */

  public approveOrder(order: any, prnVisitToggle: boolean, callback, self) {
    //TODO: See if order needs to first be updated with approved by or if that's handled elsewhere
    try {
      order.orderItems.forEach((item) => {
        this.increaseSpinnerCount();
        this.http
          .put(
            `${this.idgDataUrl}/orders/${order.id}/items/${item.id}/approve?prnVisitToggle=${prnVisitToggle}`,
            item
          )
          .subscribe(
            (data) => {
              this.decreaseSpinnerCount();
              callback(self);
            },
            (err) => {
              this.decreaseSpinnerCount();
              this.presentErrorToast(err, "Error Approving Order");
            }
          );
      });
    } catch (error) {
      console.error("Error starting order");
    }
  }

  public approveSubOrder(
    subOrder: any,
    approvalMethod,
    prnVisitToggle: boolean,
    didComplete
  ) {
    let url = "";
    let reason = subOrder.approvedReason ? subOrder.approvedReason : null;
    if (subOrder["documentId"]) {
      url = `${this.idgDataUrl}/orders/${subOrder.order.id}/items/${subOrder.id}/approve?reason=${reason}&documentId=${subOrder.documentId}&approvalMethod=${approvalMethod}&prnVisitToggle=${prnVisitToggle}`;
    } else {
      url = `${this.idgDataUrl}/orders/${subOrder.order.id}/items/${subOrder.id}/approve?reason=${reason}&approvalMethod=${approvalMethod}&prnVisitToggle=${prnVisitToggle}`;
    }
    try {
      if (reason) reason = encodeURIComponent(reason);
      this.increaseSpinnerCount();
      this.http.put(`${url}`, subOrder).subscribe(
        (data) => {
          this.decreaseSpinnerCount();
          didComplete(true);
        },
        (err) => {
          this.decreaseSpinnerCount();
          this.presentErrorToast(err, "Error Approving Sub-Order");
          didComplete(false);
        }
      );
    } catch (error) {
      didComplete(false);
    }
  }

  public deleteSubOrder(subOrder: any, didComplete) {
    try {
      let reason = subOrder.deleteReason
        ? subOrder.deleteReason
        : "Deleted before submission for approval";
      reason = encodeURIComponent(reason);
      this.increaseSpinnerCount();
      this.http
        .delete(
          `${this.idgDataUrl}/orders/${subOrder.order.id}/items/${subOrder.id}?reason=${reason}`
        )
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            didComplete(true);
          },
          (err) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(err, "Error deleting Sub-Order");
            didComplete(false);
          }
        );
    } catch (error) {
      this.decreaseSpinnerCount();
      didComplete(false);
    }
  }

  public denySubOrder(subOrder: any, didComplete) {
    try {
      let reason = subOrder.deniedReason;
      reason = encodeURIComponent(reason);
      this.increaseSpinnerCount();
      this.http
        .put(
          `${this.idgDataUrl}/orders/${subOrder.order.id}/items/${subOrder.id}/deny?reason=${reason}`,
          subOrder
        )
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            didComplete(true);
          },
          (err) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(err, "Error denying Sub-Order");
            didComplete(false);
          }
        );
    } catch (error) {
      this.decreaseSpinnerCount();
      didComplete(false);
    }
  }

  getOpenOrders(patientId): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    let params = new HttpParams()
      .set("size", "9999")
      .set("status", "OP")
      .set("sort", "startDatetime,desc")
      .set("patient_id", patientId);
    this.increaseSpinnerCount();
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/items`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            let orderItems = this.combineOrderAndTypeSpecificData(
              data["content"]
            );
            observer.next(this.convertOrderItemsToOrders(orderItems));
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(err, "Failed to load open orders.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  getOpenDiscontinueOrderItems(patientId): Observable<any> {
    //TODO: Change to get the correct stuff
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    let params = new HttpParams()
      .set("size", "9999")
      .set("status", "SU")
      .set("sort", "startDatetime,desc")
      .set("patient_id", patientId);
    this.increaseSpinnerCount();
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/items`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(
              this.combineOrderAndTypeSpecificData(data["content"])
            );
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(err, "Failed to load discontinue orders.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  performActionOnAudit(action, audit, reason, documentId = null) {
    let url = "";
    let auditId = audit.id;
    let headers = new HttpHeaders().set("Content-Type", "application/json");
    let encodedReason = encodeURIComponent(reason);
    if (action === "deny" && documentId) {
      url = `${this.idgDataUrl}/orders/audits/${auditId}/${action}?reason=${encodedReason}&document-id=${documentId}`;
    } else {
      url = `${this.idgDataUrl}/orders/audits/${auditId}/${action}?reason=${encodedReason}`;
    }
    return new Observable((observer) => {
      this.http.put(`${url}`, { headers: headers }).subscribe(
        (data) => {
          observer.next(data);
          observer.complete();
        },
        (err) => {
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  deleteAudit(audit, reason) {
    return this.performActionOnAudit("delete", audit, reason);
  }

  denyAudit(audit, reason, documentId = null) {
    return this.performActionOnAudit("deny", audit, reason, documentId);
  }

  approveAudit(audit, reason) {
    return this.performActionOnAudit("complete", audit, reason);
  }

  //when order has been successfully approved, VF Data should be sent to asmt svc to update records
  sendVFDataToASMT(data) {
    let payload = this.convertToASMTFormat(data);
    this.http
      .post(`${this.assessmentUrl}/visitfrequencies/`, payload)
      .subscribe(
        (data) => {},
        (err) => {}
      );
  }

  /** Active Orders */

  getActiveOrders(patientId, type?): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    let params = new HttpParams()
      .set("size", "100")
      .set("status", "AC")
      .set("sort", "type")
      .set("patient_id", patientId);
    this.increaseSpinnerCount();
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/items`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(
              this.combineOrderAndTypeSpecificData(data["content"])
            );

            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(err, "Failed to load active orders.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  getActiveOrdersTyped(patientId, type?): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    let params = new HttpParams()
      .set("size", "100")
      .set("status", "AC")
      .set("sort", "type")
      .set("patient_id", patientId);
    this.increaseSpinnerCount();
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/items`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(
              this.combineOrderAndTypeSpecificData(data["content"])
            );

            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(err, "Failed to load active orders.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  public discontinueSubOrder(subOrder: any, prnVisitToggle: boolean, callback) {
    try {
      let payload = {
        orderItem: { id: subOrder.id },
      };
      this.increaseSpinnerCount("Creating Discontinue Order...");
      this.http
        .post(
          `${this.idgDataUrl}/orders/audits?prnVisitToggle=${prnVisitToggle}`,
          payload
        )
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            this.presentSuccessToast("Discontinue Order Successfully Created.");
            callback();
          },
          (err) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(err, "Error Discontinuing Order");
          }
        );
    } catch (error) {}
  }

  suspendOrderItem(orderItem, payload, prnVisitToggle: boolean, didComplete) {
    let headers = new HttpHeaders().set("Content-Type", "application/json");
    try {
      this.http
        .put(
          `${this.idgDataUrl}/orders/${orderItem.order.id}/items/${orderItem.id}/suspend?prnVisitToggle=${prnVisitToggle}`,
          payload,
          { headers: headers }
        )
        .subscribe(
          (data) => {
            didComplete(true);
          },
          (err) => {
            this.presentErrorToast(err, "Failed to Suspend Order");
            didComplete(false);
          }
        );
    } catch (e) {
      this.presentErrorToast(e, "Failed to Suspend Order");
      didComplete(false);
    }
  }

  administerMedication(subOrder) {
    subOrder.administered = true;
    let medData = OrderItem.getTypeSpecificData(subOrder);
    medData.id = subOrder.orderTypeId;
    return new Observable((observer) => {
      this.http.put(`${this.idgDataUrl}/orders/medications`, medData).subscribe(
        (data) => {
          observer.next(data);
          observer.complete();
        },
        (err: HttpErrorResponse) => {
          this.presentErrorToast(err, "Error updating administered status");
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  administerStandingOrder(subOrder) {
    subOrder.administered = true;
    return new Observable((observer) => {
      this.http
        .put(
          `${this.idgDataUrl}/orders/${subOrder.order.id}/items/${subOrder.id}/administer`,
          subOrder
        )
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.presentErrorToast(err, "Error updating administered status");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  /** Discontinued Orders Tab */
  getSuspendedOrders(patientId): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    let params = new HttpParams()
      .set("size", "100")
      .set("status", "SU")
      .set("sort", "type")
      .set("patient_id", patientId);
    this.increaseSpinnerCount();
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/items`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(
              this.combineOrderAndTypeSpecificData(data["content"])
            );
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(err, "Failed to load active orders.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  getAuditsForPatient(patId): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    let params = new HttpParams()
      .set("size", "9999")
      .set("sort", "updatedDatetime")
      .set("pat_id", patId);
    this.increaseSpinnerCount();
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/audits`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(data["content"]);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(err, "Failed to load audits.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  getAuditsForPatientWithStatus(patId, status): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    let params = new HttpParams()
      .set("size", "9999")
      .set("sort", "updatedDatetime")
      .set("status", status)
      .set("pat_id", patId);
    this.increaseSpinnerCount();
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/audits`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(data["content"]);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(
              err,
              `Failed to load audits for status code ${status}.`
            );
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  /** Order History */

  getDeletedOrders(patientId): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    let params = new HttpParams()
      .set("size", "100")
      .set("status", "DE")
      .set("patient_id", patientId)
      .set("sort", "deletedDatetime,desc");
    this.increaseSpinnerCount("Loading Deleted Orders...");
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/items`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(
              this.combineOrderAndTypeSpecificData(data["content"])
            );
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(err, "Failed to load deleted orders.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  getDeniedOrders(patientId): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    let params = new HttpParams()
      .set("size", "100")
      .set("status", "DN")
      .set("patient_id", patientId)
      .set("sort", "deniedDatetime,desc");
    this.increaseSpinnerCount("Loading Denied Orders...");
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/items`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(
              this.combineOrderAndTypeSpecificData(data["content"])
            );
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(err, "Failed to load denied orders.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  getDiscontinuedOrders(patientId): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    let params = new HttpParams()
      .set("size", "100")
      .set("status", "DI")
      .set("patient_id", patientId)
      .set("sort", "discontinuedDatetime,desc");
    this.increaseSpinnerCount("Loading Discontinued Orders...");
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/items`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(
              this.combineOrderAndTypeSpecificData(data["content"])
            );
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(err, "Failed to load discontinued orders.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  getCompletedOrders(patientId): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    let params = new HttpParams()
      .set("size", "100")
      .set("status", "CO")
      .set("patient_id", patientId)
      .set("sort", "completedDatetime,desc");
    this.increaseSpinnerCount("Loading Completed Orders...");
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/items`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(
              this.combineOrderAndTypeSpecificData(data["content"])
            );
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(err, "Failed to load completed orders.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  /** Physician Panel */
  getOpenOrdersForPhysician(id, patientId = null) {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    let params;
    if (patientId) {
      params = new HttpParams()
        .set("size", "9999")
        .set("status", "OP")
        .set("sort", "startDatetime,asc")
        .set("patient_id", patientId);
    } else {
      params = new HttpParams()
        .set("size", "9999")
        .set("status", "OP")
        .set("physician_id", id)
        .set("sort", "startDatetime,asc");
    }

    this.increaseSpinnerCount();
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/items`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            let orderItems = this.combineOrderAndTypeSpecificData(
              data["content"]
            );
            let orders = this.convertOrderItemsToOrders(orderItems);
            if (patientId)
              orders.filter((order) => {
                return (
                  order.orderItems[0] &&
                  order.orderItems[0].patient.id === patientId
                );
              });
            observer.next(orders);
            observer.complete();
          },
          (err) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(err, "Failed to load orders.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  getOpenAuditsForPhysician(physId, patId = null) {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    let params;
    if (patId) {
      params = new HttpParams()
        .set("size", "9999")
        .set("status", "OP")
        .set("sort", "startedDatetime,asc")
        .set("phys_id", physId)
        .set("pat_id", patId);
    } else {
      params = new HttpParams()
        .set("size", "9999")
        .set("status", "OP")
        .set("sort", "startedDatetime,asc")
        .set("phys_id", physId);
    }

    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/audits`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            observer.next(data["content"]);
            observer.complete();
          },
          (err) => {
            this.presentErrorToast(err, "Failed to load DC orders.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  checkPhysician(id, item, callback) {
    if (item.physician && item.physician.id) {
      callback(item.physician.id === id);
    } else if (item.type === "DG") {
      this.checkDiagnosisPhysician(id, item, callback);
    } else {
      callback(false);
    }
  }

  checkDiagnosisPhysician(id, item, callback) {
    this.retrieveTypeSpecificData(item.type, item.orderTypeId).subscribe(
      (data) => {
        if (data) {
          let diagnosisPhysician = null;
          if (
            data.afterCurrentPhysician &&
            data.afterCurrentPhysician.id === id
          ) {
            diagnosisPhysician = data.afterCurrentPhysician;
            //callback(true)
          } else if (
            data.afterSecondaryPhysician &&
            data.afterSecondaryPhysician.id === id
          ) {
            diagnosisPhysician = data.afterSecondaryPhysician;
            //callback(true)
          } else if (
            data.afterRelatedPhysician &&
            data.afterRelatedPhysician.id === id
          ) {
            diagnosisPhysician = data.afterRelatedPhysician;
            //callback(true)
          }
          callback(diagnosisPhysician && diagnosisPhysician.id === id);
        } else {
          callback(false);
        }
      }
    );
  }

  getPatientInfo(patId): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return new Observable((observer) =>
      this.http
        .get(
          `${this.idgDataUrl}/company-resources/patient-charts/${patId}?externalId=false`,
          { headers: headers }
        )
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err) => {
            this.presentErrorToast(
              err,
              "Couldn't retrieve patient info from server"
            );
            observer.error(err);
            observer.complete();
          }
        )
    );
  }

  getUserInfo() {
    let headers = new HttpHeaders().set("Content-Type", "application/json");
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/company-resources/users/`, {
          headers: headers,
        })
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err) => {
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  getAuditDataForOrderItem(orderItemId) {
    let headers = new HttpHeaders().set("Content-Type", "application/json");
    let params = new HttpParams().set("order_id", orderItemId);
    return new Observable((observer) => {
      this.increaseSpinnerCount("Retrieving Discontinue Order Info...");
      this.http
        .get(`${this.idgDataUrl}/orders/audits`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(data["content"]);
            observer.complete();
          },
          (err) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(
              err,
              "Failed to Retrieve Discontinue Order Info"
            );
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  setPatientChartId(patChartId: string) {
    // NOVA-1490: guard against nullish args so we never persist "undefined" as
    // a string into sessionStorage, and use setItem so instrumentation can see
    // the write.
    if (patChartId == null) {
      return;
    }
    this.patChartId = patChartId;
    sessionStorage.setItem("patChartId", String(this.patChartId));
  }

  public getOrderById(orderId: number): Observable<Order> {
    let url = `${this.idgDataUrl}/orders/${orderId}`;
    return this.http.get<Order>(url).map((x) => {
      let o = new Order();

      o.id = x.id;
      o.name = x.name;

      for (let i = 0; i < x.orderItems.length; i++) {
        // physician: OrderPhysician;
        let t = x.orderItems[i];
        let oi = {
          id: t.id,
          orderTypeId: t.orderTypeId,
          type: t.type,
          orderStatus: t.orderStatus,
          approvalMethod: t.approvalMethod,
          createdBy: {
            id: t.createdBy.id,
            username: t.createdBy.username,
            externalId: t.createdBy.externalId,
          },
          createdDatetime: t.createdDatetime,
          startedDatetime: t.startedDatetime,
          approvedDatetime: t.approvedDatetime,
          startedBy: {
            id: t.startedBy.id,
            username: t.startedBy.username,
            externalId: t.startedBy.externalId,
          },
          approvedBy: {
            id: t.approvedBy.id,
            username: t.approvedBy.username,
            externalId: t.approvedBy.externalId,
          },
          physician: {
            id: t.physician.id,
            qbosId: t.physician.qbosId,
            providerId: t.physician.providerId,

            // should this use qbosId instead?
            professionalProvider$:
              this.resourceManagementService.getProviderByExternalId(
                t.physician.qbosId
              ),
            //                        professionalProvider$: this.resourceManagementService.getProfessionalProvider(t.physician.providerId)
          },
        };

        o.orderItems.push(oi);
      }

      return o;
    });
  }

  /**
   * ENDPOINT CURRRENTLY DOWSN"T WORK (RETURNS NULL)
   * @param orderId
   */
  public getOrder(orderId: number): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return new Observable((observer) => {
      this.increaseSpinnerCount();
      this.http
        .get(`${this.idgDataUrl}/orders/${orderId}`, { headers: headers })
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(data["content"]);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.presentErrorToast(err, "Failed to load orders.");
            this.decreaseSpinnerCount();
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  /** Printing */
  public generateMedReport(patId, patientId) {
    return new Observable((observer) => {
      this.increaseSpinnerCount("Generating Report...");
      this.http
        .get(
          `${this.idgDataUrl}/orders/patient-charts/${patId}/generate_medication_profile_report?pchartId=${patientId}`,
          { responseType: "blob" }
        )
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.presentErrorToast(err, "Failed to generate");
            this.decreaseSpinnerCount();
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  //Function to generate Order Report
  public generateOrderReport(orderId, orderitemId) {
    let params = new HttpParams();
    if (orderitemId)
      params = new HttpParams().set("order_item_id", orderitemId);
    return new Observable((observer) => {
      this.increaseSpinnerCount("Generating Report...");
      this.http
        .get(`${this.idgDataUrl}/orders/${orderId}/generate_order_report`, {
          params: params,
          responseType: "blob",
        })
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.presentErrorToast(err, "Failed to generate");
            this.decreaseSpinnerCount();
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  public generateActiveOrders(payload) {
    this.increaseSpinnerCount("Downloading Report");
    this.http
      .post(`${this.reportsUrl}/reports/active_order`, payload, {
        responseType: "blob",
      })
      .subscribe(
        (response) => {
          let blob = new Blob([response], { type: "application/pdf" });
          saveAs(blob, "active_orders");
          this.decreaseSpinnerCount();
        },
        (e) => {
          console.error(e);
          this.decreaseSpinnerCount();
        }
      );
  }

  public getOrders(): Observable<any> {
    //TODO: make more specific - get orders for patient
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return new Observable((observer) => {
      this.increaseSpinnerCount();
      this.http
        .get(`${this.idgDataUrl}/orders?size=100`, { headers: headers })
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(data["content"]);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.presentErrorToast(err, "Failed to load orders.");
            this.decreaseSpinnerCount();
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  public deleteOrder(order: any, didComplete) {
    try {
      let reason = order.deleteReason
        ? order.deleteReason
        : "Deleted before approval due to inaccuracy";
      reason = encodeURIComponent(reason);
      let length = order.orderItems.length;
      let count = 1;
      order.orderItems.forEach((item) => {
        this.increaseSpinnerCount();
        this.http
          .delete(
            `${this.idgDataUrl}/orders/${order.id}/items/${item.id}?reason=${reason}`
          )
          .subscribe(
            (data) => {
              this.decreaseSpinnerCount();
              if (count === length) {
                didComplete(true);
              }
              count++;
            },
            (err) => {
              this.decreaseSpinnerCount();
              this.presentErrorToast(err, "Error Deleting Order");
              didComplete(false);
            }
          );
      });
    } catch (error) {
      didComplete(false);
    }
  }

  public getSubOrders(): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return new Observable((observer) => {
      this.increaseSpinnerCount();
      this.http
        .get(`${this.idgDataUrl}/orders?size=100`, { headers: headers })
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            let orders = data["content"];
            let output = [];
            orders.forEach((order) => {
              if (order.orderItems.length !== 0) {
                order.orderItems.forEach((subOrder) => output.push(subOrder));
              }
            });
            observer.next(output);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.presentErrorToast(err, "Failed to load orders.");
            this.decreaseSpinnerCount();
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  /** Enums */
  getOrderStatusEnums() {
    return new Observable<any>((observer) => {
      this.increaseSpinnerCount();
      this.http.get(`${this.idgDataUrl}/enums/orderStatuses`).subscribe(
        (data) => {
          this.decreaseSpinnerCount();
          observer.next(data);
          observer.complete();
        },
        (err: HttpErrorResponse) => {
          this.presentErrorToast(err, "Failed to load Order Status Enums");
          this.decreaseSpinnerCount();
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  getOrderCategoryEnums() {
    return new Observable<any>((observer) => {
      this.http.get(`${this.idgDataUrl}/enums/categories`).subscribe(
        (data) => {
          observer.next(data);
          observer.complete();
        },
        (err: HttpErrorResponse) => {
          this.presentErrorToast(err, "Failed to load Order Category Enums");
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  getOrderTypeEnums() {
    return new Observable<any>((observer) => {
      this.http.get(`${this.idgDataUrl}/enums/orderTypes`).subscribe(
        (data) => {
          observer.next(data);
          observer.complete();
        },
        (err: HttpErrorResponse) => {
          this.presentErrorToast(err, "Failed to load Order Type Enums");
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  getRoleEnums() {
    return new Observable<any>((observer) => {
      //this.increaseSpinnerCount();
      this.http.get(`${this.idgDataUrl}/enums/roles`).subscribe(
        (data) => {
          this.roleEnums = data;
          //this.decreaseSpinnerCount();
          observer.next(data);
          observer.complete();
        },
        (err: HttpErrorResponse) => {
          this.presentErrorToast(err, "Failed to load Role Enums");
          //this.decreaseSpinnerCount();
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  getDiagnosisEnums() {
    return new Observable<any>((observer) => {
      //this.increaseSpinnerCount();
      this.http.get(`${this.idgDataUrl}/enums/diagnosisTypes`).subscribe(
        (data) => {
          //this.decreaseSpinnerCount();
          observer.next(data);
          observer.complete();
        },
        (err: HttpErrorResponse) => {
          this.presentErrorToast(err, "Failed to load Diagnosis Enums");
          //this.decreaseSpinnerCount();
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  /**MAR*/
  getMedicationOrders(patId) {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    let params = new HttpParams()
      .set("size", "9999")
      .set("status", "OP")
      .set("sort", "startDatetime,desc")
      .set("patient_id", patId);
    this.increaseSpinnerCount();
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/items`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            params = new HttpParams()
              .set("size", "9999")
              .set("status", "AC")
              .set("sort", "startDatetime,desc")
              .set("patient_id", patId);
            this.http
              .get(`${this.idgDataUrl}/orders/items`, {
                headers: headers,
                params: params,
              })
              .subscribe(
                (data2) => {
                  this.decreaseSpinnerCount();
                  let output = this.combineOrderAndTypeSpecificData(
                    data["content"]
                  );
                  this.combineOrderAndTypeSpecificData(
                    data2["content"]
                  ).forEach((order) => output.push(order));
                  observer.next(output.filter((order) => order.type === "MC"));
                  observer.complete();
                },
                (err) => {
                  this.decreaseSpinnerCount();
                  this.presentErrorToast(err, "Failed to load active orders.");
                  observer.next(
                    this.combineOrderAndTypeSpecificData(
                      data["content"]
                    ).filter((order) => order.type === "MC")
                  );
                  observer.complete();
                }
              );
          },
          (err: HttpErrorResponse) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(err, "Failed to load open orders.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  createMAR(marData) {
    return new Observable((observer) => {
      this.http
        .post(`${this.idgDataUrl}/orders/medications/admin/records`, marData)
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err) => {
            this.presentErrorToast(err, "Failed to create new record");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  getMARs() {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    let params = new HttpParams().set("size", "9999");
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/medications/admin/records`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            observer.next(data["content"]);
            observer.complete();
          },
          (err) => {
            this.presentErrorToast(err, "Failed to retrieve MAR");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  /** Utility Functions */

  public convertToASMTFormat(orderItem) {
    if (this.roleEnums.length === 0) this.getRoleEnums();
    let roleLookupCode;
    const visitCountLookupCode = visitFreqConfig.inputs.visits.options.find(
      (option) => option.display === orderItem.visits
    ).value;
    const visitIntervalLookupCode =
      visitFreqConfig.inputs.timeInterval.options.find(
        (option) => option.display === orderItem.timeInterval
      ).value;
    const visitPrnLookupCode = visitFreqConfig.inputs.prnVisits.options.find(
      (option) => option.display === orderItem.prnVisits
    ).value;
    const reasonLookupCode = [
      visitFreqConfig.inputs.reason.options.find(
        (option) => option.display === orderItem.reason
      ).value,
    ];
    if (orderItem.otherRole !== null) {
      roleLookupCode = orderItem.otherRole;
    } else {
      roleLookupCode = this.roleEnums[orderItem.role]
        ? this.roleEnums[orderItem.role]["lookup"]
        : null;
    }

    let payload = {
      patChartId: parseInt(orderItem.patient.qbosId),
      roleLookupCode: roleLookupCode,
      visitCountLookupCode: visitCountLookupCode,
      visitIntervalLookupCode: visitIntervalLookupCode,
      visitPrnLookupCode: visitPrnLookupCode,
      reasonLookupCode: reasonLookupCode,
      startedDte: moment(orderItem.itemStartDatetime).format("YYYYMMDD"),
      completedDte: orderItem.itemDiscontinueDatetime
        ? moment(orderItem.itemDiscontinueDatetime).format("YYYYMMDD")
        : null,
    };

    return payload;
  }

  public convertOrderItemsToOrders(orderItems: [any]) {
    let orders = [];
    if (orderItems) {
      orderItems.forEach((item) => {
        let orderId = item.order.id;
        let order = orders.find((order) => order.id === orderId);
        if (order) {
          order.orderItems.push(item);
        } else {
          orders.push(this.createNewOrder(orderId, item));
        }
      });
    }
    return orders;
  }

  private combineOrderAndTypeSpecificData(orderItems) {
    return orderItems.map((item) => {
      let oi = item.orderItem;
      // Condition to check if standing order object is present in the payload, if it is present,
      // then append the standing order object to the Item Object
      if (item.standingOrderIdAndVersionName)
        oi.standingOrderIdAndVersionName = item.standingOrderIdAndVersionName;
      let typeInfo = item.orderTypeDetails;
      delete typeInfo.id;
      delete typeInfo.deletedDatetime;
      let data = {
        ...oi,
        ...typeInfo,
      };
      return data;
    });
  }

  private createNewOrder(orderId, item) {
    return { id: orderId, orderItems: [item] };
  }

  /**Error and Spinner Functions*/

  presentErrorToast(err: HttpErrorResponse, message: string) {
    if (err && (err as any).code === "REFRESH_TOKEN_FAILED") {
      return;
    }
    message += " ";
    if (err.error instanceof Error) {
      message += "Client error.";
    } else {
      message += " ";
      if (err.status == 401) {
        message += "User not authorized.";
      } else if (err.status >= 500) {
        message += "Server error.";
      } else {
        message += "Communication error.";
      }
    }

    let toast = this.toastCtrl.create({
      message: message,
      duration: 4000,
      position: "below",
      cssClass: "error",
    });

    toast.present();
  }

  presentSuccessToast(message: string) {
    let toast = this.toastCtrl.create({
      message: message,
      duration: 4000,
      position: "below",
      cssClass: "success",
    });

    toast.present();
  }

  public increaseSpinnerCount(message: string = "Loading...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  public decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0 && this.spinner) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  /* Standing Orders API */
  public createStandingOrder(data: any) {
    this.increaseSpinnerCount("Saving...");
    if (data) {
      return this.http.post(`${this.idgDataUrl}/orders/standingOrder`, data);
    }
  }

  public getAllStandingOrders(): Observable<any> {
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/standingOrder/all`, {
          headers: this.headers,
        })
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.presentErrorToast(err, "Failed to load standing orders.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  public getActiveStandingOrder(): Observable<any> {
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/standingOrder/active`, {
          headers: this.headers,
        })
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.presentErrorToast(err, "Failed to load standing orders.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  public createStandingOrders(data: any, standingOrderId) {
    if (data) {
      return this.http.post(
        `${this.idgDataUrl}/orders/standingOrder/${standingOrderId}/orders`,
        data
      );
    }
  }

  public discontinueStandingOrder(standingOrderId): Observable<any> {
    this.increaseSpinnerCount();
    return new Observable((observer) => {
      this.http
        .put(
          `${this.idgDataUrl}/orders/standingOrder/${standingOrderId}/discontinue`,
          { headers: this.headers }
        )
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.decreaseSpinnerCount();
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  public getAllPhysician(): Observable<any> {
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/company-resources/persons/physicians/all`, {
          headers: this.headers,
        })
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.presentErrorToast(err, "Failed to load all Physician.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }
  public downloadReport(data) {
    let startDate = moment(data["startDate"]).format("MMYY");
    let endDate = moment(data["endDate"]).format("MMYY");
    let headers = new HttpHeaders();
    headers = headers.set("Accept", "application/octet-stream");
    let fileName = `Orders_${startDate}_${endDate}.zip`;
    this.increaseSpinnerCount("Downloading...");
    return new Observable((observer) => {
      this.http
        .post(`${this.idgDataUrl}/orders/batch`, data, {
          headers,
          responseType: "blob",
        })
        .subscribe(
          (response) => {
            let file = new Blob([response], {
              type: "application/octet-stream",
            });
            saveAs(file, fileName);
            this.decreaseSpinnerCount();
            observer.next(response);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(err, "Failed to download.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }
  public getOpenOrdersCount(qbosId): Observable<any> {
    const cacheKey = `${qbosId}`;
    if (this.openOrdersCache.has(cacheKey)) {
      return this.openOrdersCache.get(cacheKey);
    }
    const shared$ = this.http
      .get(`${this.idgDataUrl}/orders/patient-charts/${qbosId}/brief`, {
        headers: this.headers,
      })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          this.presentErrorToast(err, "Failed to get open orders count.");
          return of(err);
        }),
        finalize(() => this.openOrdersCache.delete(cacheKey)),
        shareReplay(1)
      );
    this.openOrdersCache.set(cacheKey, shared$);
    return shared$;
  }

  public getCarryThroughCreatinineData(): Observable<CreatinineData> {
    const mockCreatinine = MockCreatinineData;
    return of(mockCreatinine).pipe(delay(2000));
  }

  public createRisk(data: any): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");
    let params = new HttpParams().set("size", "9999");

    return new Observable((observer) => {
      this.http
        .post(`${this.idgDataUrl}/bereaved-management/bereavement`, data)
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.presentErrorToast(err, "Failed Create Risk");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  /**
   * Method to get active order(s) which has type 'Visit Frequency'
   * @param patientId
   * @param pageSize
   * @param currentPage
   */
  getVisitFrequencyActiveOrders(
    patientId,
    pageSize,
    currentPage
  ): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    let params = new HttpParams()
      .set("size", pageSize)
      .set("status", "AC")
      .set("sort", "startDatetime,DESC")
      .set("page", currentPage)
      .set("patient_id", patientId)
      .set("type", "VF");
    this.increaseSpinnerCount();
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/items`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(err, "Failed to load active orders.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  moveToOpenFromActive(subOrder, prnVisitToggle: boolean) {
    this.increaseSpinnerCount();
    return new Observable((observer) => {
      this.http
        .put(
          `${this.idgDataUrl}/orders/${subOrder.order.id}/items/${subOrder.id}/open?reason=${subOrder.reason}&prnVisitToggle=${prnVisitToggle}`,
          { headers: this.headers }
        )
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.decreaseSpinnerCount();
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  getMedicationAdminConfig() {
    return new Observable((observer) => {
      this.http
        .get(`${this.idgDataUrl}/orders/configs/med-admin-config`, {
          headers: this.headers,
        })
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.presentErrorToast(err, "Failed to load Medication config.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  getPhysiciansIdgDetails(ids: string) {
    return this.http.get(
      `${this.idgDataUrl}/company-resources/persons/providers?providers=${ids}`
    );
  }

  getMedicationListByPatientId(patientId) {
    this.increaseSpinnerCount();
    return new Observable((observer) => {
      this.http
        .get(
          `${this.idgDataUrl}/orders/medications/summary?patientId=${patientId}`,
          { headers: this.headers }
        )
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.decreaseSpinnerCount();
            this.presentErrorToast(err, "Failed to load Medication List.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/order-data-service/order-data-service.ts